import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Stack,
  Link
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { fetchJson, resolveApiUrl } from '../../../utils/fetchJson';
import { UserContext } from '../../../contexts/UserContext';

export default function ManagePaymentsPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [years, setYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  const [manualAmount, setManualAmount] = useState('');
  const [manualPayerName, setManualPayerName] = useState('');
  const [manualNotes, setManualNotes] = useState('');
  const [savingManual, setSavingManual] = useState(false);

  // School-submitted offline payments awaiting review (across all schools)
  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);

  // Reject dialog
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [shortfallTarget, setShortfallTarget] = useState(null);
  const [activatingAnyway, setActivatingAnyway] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceDueDate, setBalanceDueDate] = useState('');
  const [balanceError, setBalanceError] = useState('');

  useEffect(() => {
    loadYears();
    loadPending();
  }, []);

  useEffect(() => {
    if (!selectedYearId) return;
    loadAnalytics(selectedYearId);
  }, [selectedYearId]);

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('billing');
  }, [user]);

  async function loadYears() {
    try {
      const data = await fetchJson('/api/global-academic-years');
      const list = Array.isArray(data) ? data : (Array.isArray(data?.years) ? data.years : []);
      setYears(list);
      const now = new Date();
      const covering = list.find((y) => {
        const s = y.startsAt ? new Date(y.startsAt) : null;
        const e = y.endsAt ? new Date(y.endsAt) : null;
        return s && e && s <= now && now <= e;
      });
      const current = covering || list.find(y => !!y.isCurrent) || list[list.length - 1] || null;
      if (current?.id) {
        setSelectedYearId(String(current.id));
      }
    } catch (e) {
      setError(e?.message || t('managePayments.loadYearsFailed', 'Failed to load academic years'));
    }
  }

  async function loadAnalytics(yearId) {
    setLoading(true);
    setError('');
    try {
      const data = await fetchJson(`/schools/cross-school-analytics?academicYearId=${encodeURIComponent(String(yearId))}`);
      const list = Array.isArray(data?.schools) ? data.schools : [];
      setRows(list.map((s, idx) => ({ id: s.id ?? idx, ...s })));
    } catch (e) {
      setError(e?.message || t('managePayments.loadFailed', 'Failed to load payment status'));
    } finally {
      setLoading(false);
    }
  }

  async function loadPending() {
    setLoadingPending(true);
    try {
      const data = await fetchJson('/api/subscriptions/pending-payments');
      setPending(Array.isArray(data?.payments) ? data.payments : []);
    } catch (e) {
      // Non-fatal: the rest of the page still works
    } finally {
      setLoadingPending(false);
    }
  }

  async function approvePayment(paymentId, fromPending = false, { activateAnyway = false, balanceAmount = 0, balanceDueDate = null } = {}) {
    if (!paymentId || !canManage) return;
    try {
      const res = await fetchJson(
        `/api/subscriptions/payments/${encodeURIComponent(String(paymentId))}/validate-offline`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ activateAnyway, balanceAmount, balanceDueDate })
        }
      );

      await loadPending();
      if (fromPending && selectedYearId) await loadAnalytics(selectedYearId);
      if (selectedSchool) await loadDetails(selectedSchool);

      // The payment is validated but the subscription was NOT renewed because the
      // amount doesn't cover the invoice. Surface it — this used to pass silently
      // and left schools un-renewed after an apparently successful approval.
      if (res && res.activated === false) {
        const shortfall = Number(res.shortfall || 0);
        setShortfallTarget({
          paymentId,
          fromPending,
          invoiceTotal: Number(res.invoiceTotal || 0),
          paidAmount: Number(res.paidAmount || 0),
          shortfall
        });
        // Default to carrying the whole shortfall as a debt: the common case is a
        // part payment the school still owes, not a discount.
        setBalanceAmount(String(shortfall));
        setBalanceDueDate('');
        setBalanceError('');
      }
    } catch (e) {
      setError(e?.message || t('managePayments.validateFailed', 'Failed to validate payment'));
    }
  }

  async function confirmActivateWithBalance() {
    const target = shortfallTarget;
    if (!target) return;

    const amount = Number(balanceAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      setBalanceError(t('managePayments.balanceInvalid', 'Enter a valid balance amount.'));
      return;
    }
    if (amount > target.shortfall + 0.001) {
      setBalanceError(t('managePayments.balanceTooHigh', 'Balance cannot exceed the outstanding amount.'));
      return;
    }
    // A debt with no deadline is not a debt anyone can act on.
    if (amount > 0 && !balanceDueDate) {
      setBalanceError(t('managePayments.balanceDateRequired', 'Set the date this balance must be paid by.'));
      return;
    }

    setBalanceError('');
    setActivatingAnyway(true);
    try {
      await approvePayment(target.paymentId, target.fromPending, {
        activateAnyway: true,
        balanceAmount: amount,
        balanceDueDate: amount > 0 ? balanceDueDate : null
      });
      setShortfallTarget(null);
    } finally {
      setActivatingAnyway(false);
    }
  }

  function openReject(payment) {
    setRejectTarget(payment);
    setRejectReason('');
    setRejectOpen(true);
  }

  async function confirmReject() {
    if (!rejectTarget?.id || !canManage) return;
    setRejecting(true);
    try {
      await fetchJson(`/api/subscriptions/payments/${encodeURIComponent(String(rejectTarget.id))}/reject-offline`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason || null })
      });
      setRejectOpen(false);
      setRejectTarget(null);
      setRejectReason('');
      await loadPending();
      if (selectedSchool) await loadDetails(selectedSchool);
      if (selectedYearId) await loadAnalytics(selectedYearId);
    } catch (e) {
      setError(e?.message || t('managePayments.rejectFailed', 'Failed to reject payment'));
    } finally {
      setRejecting(false);
    }
  }

  const summary = useMemo(() => {
    const paid = rows.filter(r => String(r.paymentStatus).toLowerCase() === 'paid').length;
    const partial = rows.filter(r => String(r.paymentStatus).toLowerCase() === 'partial').length;
    const unpaid = rows.filter(r => String(r.paymentStatus).toLowerCase() === 'unpaid').length;
    const expected = rows.reduce((sum, r) => sum + Number(r.expectedAmount || 0), 0);
    const paidAmt = rows.reduce((sum, r) => sum + Number(r.paidAmount ?? r.revenue ?? 0), 0);
    const outstanding = rows.reduce((sum, r) => sum + Number(r.outstandingAmount || 0), 0);
    return { paid, partial, unpaid, expected, paidAmt, outstanding };
  }, [rows]);

  const openDetails = async (row) => {
    setSelectedSchool(row);
    setDetailsOpen(true);
    await loadDetails(row);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedSchool(null);
    setPayments([]);
    setDetailsError('');
    setManualAmount('');
    setManualPayerName('');
    setManualNotes('');
  };

  async function loadDetails(row) {
    if (!row?.id) return;
    if (!selectedYearId) return;
    setLoadingDetails(true);
    setDetailsError('');
    try {
      const data = await fetchJson(`/api/subscriptions/payments?schoolId=${encodeURIComponent(String(row.id))}&academicYearId=${encodeURIComponent(String(selectedYearId))}`);
      setPayments(Array.isArray(data?.payments) ? data.payments : []);
    } catch (e) {
      setDetailsError(e?.message || t('managePayments.loadDetailsFailed', 'Failed to load payments'));
    } finally {
      setLoadingDetails(false);
    }
  }

  async function createManualPayment() {
    if (!selectedSchool?.id) return;
    if (!canManage) return;
    const amount = Number(manualAmount || 0);
    if (!(amount > 0)) {
      setDetailsError(t('managePayments.amountError', 'Amount must be greater than 0'));
      return;
    }
    setSavingManual(true);
    setDetailsError('');
    try {
      await fetchJson('/api/subscriptions/manual-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          schoolId: Number(selectedSchool.id),
          academicYearId: Number(selectedYearId),
          amount,
          payerName: manualPayerName || null,
          notes: manualNotes || null
        })
      });
      setManualAmount('');
      setManualPayerName('');
      setManualNotes('');
      await loadDetails(selectedSchool);
      await loadAnalytics(selectedYearId);
    } catch (e) {
      setDetailsError(e?.message || t('managePayments.manualFailed', 'Failed to create manual payment'));
    } finally {
      setSavingManual(false);
    }
  }

  const columns = [
    { field: 'name', headerName: t('managePayments.school', 'School'), flex: 1, minWidth: 220 },
    { field: 'studentsCount', headerName: t('managePayments.students', 'Students'), width: 110, type: 'number' },
    {
      field: 'expectedAmount',
      headerName: t('managePayments.expected', 'Expected (XAF)'),
      width: 160,
      valueGetter: (params) => Number(params?.row?.expectedAmount || 0).toLocaleString(),
    },
    {
      field: 'paidAmount',
      headerName: t('managePayments.paid', 'Paid (XAF)'),
      width: 150,
      valueGetter: (params) => Number(params?.row?.paidAmount ?? params?.row?.revenue ?? 0).toLocaleString(),
    },
    {
      field: 'outstandingAmount',
      headerName: t('managePayments.outstanding', 'Outstanding (XAF)'),
      width: 170,
      valueGetter: (params) => Number(params?.row?.outstandingAmount || 0).toLocaleString(),
    },
    {
      field: 'paymentStatus',
      headerName: t('managePayments.status', 'Status'),
      width: 120,
      renderCell: (params) => {
        const s = String(params?.row?.paymentStatus || '').toLowerCase();
        const color = s === 'paid' ? 'success' : s === 'partial' ? 'warning' : s === 'unpaid' ? 'error' : 'default';
        const label = (params?.row?.paymentStatus ? String(params.row.paymentStatus) : 'n/a').toUpperCase();
        return <Chip size="small" label={label} color={color} />;
      }
    },
    {
      field: 'actions',
      headerName: t('managePayments.actions', 'Actions'),
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button size="small" variant="outlined" onClick={() => openDetails(params.row)}>
          {t('managePayments.viewManage', 'View / Manage')}
        </Button>
      )
    }
  ];

  const paymentColumns = [
    { field: 'id', headerName: t('managePayments.id', 'ID'), width: 80 },
    {
      field: 'amount',
      headerName: t('managePayments.amount', 'Amount (XAF)'),
      width: 150,
      valueGetter: (params) => Number(params?.row?.amount || 0).toLocaleString(),
    },
    { field: 'provider', headerName: t('managePayments.provider', 'Provider'), width: 120 },
    { field: 'status', headerName: t('managePayments.status', 'Status'), width: 120 },
    {
      field: 'createdAt',
      headerName: t('managePayments.created', 'Created'),
      width: 150,
      valueGetter: (params) => params?.row?.createdAt ? new Date(params.row.createdAt).toLocaleString() : '',
    },
    {
      field: 'invoiceNumber',
      headerName: t('managePayments.invoice', 'Invoice'),
      width: 150,
      valueGetter: (params) => params?.row?.Invoice?.invoiceNumber || params?.row?.invoiceNumber || '',
    },
    {
      field: 'proofUrl',
      headerName: t('managePayments.proof', 'Proof'),
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => params?.row?.proofUrl ? (
        <Link href={resolveApiUrl(params.row.proofUrl)} target="_blank" rel="noopener">
          {t('managePayments.view', 'View')}
        </Link>
      ) : null
    },
    {
      field: 'action',
      headerName: t('managePayments.action', 'Action'),
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const provider = String(params?.row?.provider || '').toLowerCase();
        const status = String(params?.row?.status || '').toLowerCase();
        const isOfflinePending = provider === 'offline' && status === 'pending';
        if (!isOfflinePending) return null;
        return (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={() => approvePayment(params.row.id)} disabled={!canManage}>
              {t('managePayments.approve', 'Approve')}
            </Button>
            <Button size="small" variant="outlined" color="error" onClick={() => openReject(params.row)} disabled={!canManage}>
              {t('managePayments.reject', 'Reject')}
            </Button>
          </Stack>
        );
      }
    }
  ];

  const pendingColumns = [
    { field: 'createdAt', headerName: t('managePayments.created', 'Submitted'), width: 150, valueGetter: (p) => p?.row?.createdAt ? new Date(p.row.createdAt).toLocaleString() : '' },
    { field: 'school', headerName: t('managePayments.school', 'School'), flex: 1, minWidth: 180, valueGetter: (p) => p?.row?.School?.name || p?.row?.schoolId || '' },
    { field: 'amount', headerName: t('managePayments.amount', 'Amount (XAF)'), width: 140, valueGetter: (p) => Number(p?.row?.amount || 0).toLocaleString() },
    { field: 'payerName', headerName: t('managePayments.payerName', 'Payer name'), width: 150 },
    { field: 'providerReference', headerName: t('managePayments.reference', 'Reference'), width: 150 },
    {
      field: 'proofUrl',
      headerName: t('managePayments.proof', 'Proof'),
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => params?.row?.proofUrl ? (
        <Link href={resolveApiUrl(params.row.proofUrl)} target="_blank" rel="noopener">
          {t('managePayments.view', 'View')}
        </Link>
      ) : null
    },
    {
      field: 'action',
      headerName: t('managePayments.action', 'Action'),
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" onClick={() => approvePayment(params.row.id, true)} disabled={!canManage}>
            {t('managePayments.approve', 'Approve')}
          </Button>
          <Button size="small" variant="outlined" color="error" onClick={() => openReject(params.row)} disabled={!canManage}>
            {t('managePayments.reject', 'Reject')}
          </Button>
        </Stack>
      )
    }
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5">{t('managePayments.title', 'Manage Payments')}</Typography>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>{t('managePayments.academicYear', 'Academic Year')}</InputLabel>
          <Select
            label={t('managePayments.academicYear', 'Academic Year')}
            value={selectedYearId}
            onChange={(e) => setSelectedYearId(e.target.value)}
          >
            {years.map(y => (
              <MenuItem key={y.id} value={String(y.id)}>
                {y.name}{y.isCurrent ? ` (${t('managePayments.current', 'Current')})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <Chip label={`${t('managePayments.paidLabel', 'Paid')}: ${summary.paid}`} color="success" />
        <Chip label={`${t('managePayments.partialLabel', 'Partial')}: ${summary.partial}`} color="warning" />
        <Chip label={`${t('managePayments.unpaidLabel', 'Unpaid')}: ${summary.unpaid}`} color="error" />
        <Chip label={`${t('managePayments.expectedLabel', 'Expected')}: XAF ${Math.round(summary.expected).toLocaleString()}`} />
        <Chip label={`${t('managePayments.paidLabel', 'Paid')}: XAF ${Math.round(summary.paidAmt).toLocaleString()}`} />
        <Chip label={`${t('managePayments.outstandingLabel', 'Outstanding')}: XAF ${Math.round(summary.outstanding).toLocaleString()}`} color={summary.outstanding > 0 ? 'warning' : 'default'} />
      </Stack>

      {/* School-submitted payments awaiting review */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6">{t('managePayments.pendingTitle', 'Payment Submissions Awaiting Review')}</Typography>
          <Chip size="small" color={pending.length ? 'warning' : 'default'} label={pending.length} />
        </Box>
        {loadingPending ? (
          <Box sx={{ textAlign: 'center', mt: 2 }}><CircularProgress size={24} /></Box>
        ) : pending.length === 0 ? (
          <Alert severity="success">{t('managePayments.noPending', 'No payment submissions are awaiting review.')}</Alert>
        ) : (
          <Box sx={{ height: 320, width: '100%' }}>
            <DataGrid
              rows={pending.map((p, idx) => ({ id: p.id ?? idx, ...p }))}
              columns={pendingColumns}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 25]}
              disableSelectionOnClick
            />
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ height: 580, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
          />
        </Box>
      )}

      <Dialog open={detailsOpen} onClose={closeDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedSchool ? `${t('managePayments.title', 'Manage Payments')} - ${selectedSchool.name}` : t('managePayments.title', 'Manage Payments')}
        </DialogTitle>
        <DialogContent>
          {detailsError && <Alert severity="error" sx={{ mb: 2 }}>{detailsError}</Alert>}

          {selectedSchool && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
              <Chip label={`${t('managePayments.students', 'Students')}: ${Number(selectedSchool.studentsCount || 0).toLocaleString()}`} />
              <Chip label={`${t('managePayments.expectedLabel', 'Expected')}: XAF ${Number(selectedSchool.expectedAmount || 0).toLocaleString()}`} />
              <Chip label={`${t('managePayments.paidLabel', 'Paid')}: XAF ${Number(selectedSchool.paidAmount ?? selectedSchool.revenue ?? 0).toLocaleString()}`} />
              <Chip label={`${t('managePayments.outstandingLabel', 'Outstanding')}: XAF ${Number(selectedSchool.outstandingAmount || 0).toLocaleString()}`} color={Number(selectedSchool.outstandingAmount || 0) > 0 ? 'warning' : 'default'} />
              <Chip label={`${t('managePayments.status', 'Status')}: ${(selectedSchool.paymentStatus || 'n/a').toUpperCase()}`} />
            </Stack>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('managePayments.manualEntry', 'Manual Payment Entry (offline)')}</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('managePayments.amount', 'Amount (XAF)')}
                type="number"
                value={manualAmount}
                onChange={(e) => setManualAmount(e.target.value)}
                fullWidth
                disabled={!canManage}
              />
              <TextField
                label={t('managePayments.payerName', 'Payer name')}
                value={manualPayerName}
                onChange={(e) => setManualPayerName(e.target.value)}
                fullWidth
                disabled={!canManage}
              />
            </Stack>
            <TextField
              sx={{ mt: 2 }}
              label={t('managePayments.notes', 'Notes')}
              value={manualNotes}
              onChange={(e) => setManualNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              disabled={!canManage}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={createManualPayment} disabled={savingManual || !selectedSchool?.id || !canManage}>
                {t('managePayments.addPayment', 'Add Payment')}
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('managePayments.payments', 'Payments')}</Typography>
          {loadingDetails ? (
            <Box sx={{ textAlign: 'center', mt: 2 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ height: 360, width: '100%' }}>
              <DataGrid
                rows={payments.map((p, idx) => ({ id: p.id ?? idx, ...p }))}
                columns={paymentColumns}
                pageSize={10}
                rowsPerPageOptions={[10, 25]}
                disableSelectionOnClick
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails}>{t('managePayments.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Reject submission dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('managePayments.rejectTitle', 'Reject Payment')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('managePayments.rejectHint', 'The school will see this reason and can submit a corrected payment.')}
          </Typography>
          <TextField
            label={t('managePayments.rejectReason', 'Reason')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>{t('managePayments.cancel', 'Cancel')}</Button>
          <Button variant="contained" color="error" onClick={confirmReject} disabled={rejecting}>
            {t('managePayments.confirmReject', 'Reject Payment')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* The payment was accepted but does not cover the invoice, so the subscription
          is still not renewed. Renewing anyway writes off the balance, so it is an
          explicit decision rather than something that happens quietly. */}
      <Dialog open={!!shortfallTarget} onClose={() => setShortfallTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('managePayments.shortfallTitle', 'Payment approved — subscription not renewed')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t(
              'managePayments.shortfallBody',
              'The payment was recorded, but it does not cover the invoice total, so the subscription has not been renewed.'
            )}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('managePayments.invoiceTotal', 'Invoice total')}
            </Typography>
            <Typography variant="body2">{Number(shortfallTarget?.invoiceTotal || 0).toLocaleString()}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('managePayments.paidSoFar', 'Paid so far')}
            </Typography>
            <Typography variant="body2">{Number(shortfallTarget?.paidAmount || 0).toLocaleString()}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('managePayments.outstanding', 'Outstanding')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {Number(shortfallTarget?.shortfall || 0).toLocaleString()}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'managePayments.shortfallHint',
              'You can activate the school now and record the balance it still owes. The school is notified by message and email with the amount and the deadline. Reduce the balance below to write off part of it.'
            )}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label={t('managePayments.balanceAmount', 'Balance still owed')}
              value={balanceAmount}
              onChange={(e) => setBalanceAmount(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              sx={{ flex: '1 1 200px' }}
            />
            <TextField
              label={t('managePayments.balanceDueDate', 'To be paid by')}
              value={balanceDueDate}
              onChange={(e) => setBalanceDueDate(e.target.value)}
              type="date"
              InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 200px' }}
            />
          </Box>

          {Number(balanceAmount) === 0 && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
              {t('managePayments.writeOffWarning', 'A balance of 0 writes off the full outstanding amount.')}
            </Typography>
          )}
          {balanceError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {balanceError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShortfallTarget(null)}>
            {t('managePayments.leaveUnrenewed', 'Leave unrenewed')}
          </Button>
          <Button variant="contained" onClick={confirmActivateWithBalance} disabled={activatingAnyway}>
            {t('managePayments.activateWithBalance', 'Activate school')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
